import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { LayoutDashboard, Ticket, ShieldCheck, Star, CalendarCheck2, MessageSquare } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { SupportOverviewTab } from "./SupportOverviewTab";
import { TicketsTab } from "./TicketsTab";
import { WarrantyTab } from "./WarrantyTab";
import { FeedbackTab } from "./FeedbackTab";
import { FollowUpsTab } from "./FollowUpsTab";
import { CommunicationTab } from "./CommunicationTab";
import { NewTicketModal } from "./NewTicketModal";

const TAB_DEFS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "tickets", label: "Tickets", icon: Ticket },
  { id: "warranty", label: "Warranty", icon: ShieldCheck },
  { id: "feedback", label: "Feedback", icon: Star },
  { id: "follow_ups", label: "Follow-ups", icon: CalendarCheck2 },
  { id: "communication", label: "Communication", icon: MessageSquare },
];

export const SupportWorkspace = ({ accessLevel }) => {
  const { API } = useAuth();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [newTicketCustomer, setNewTicketCustomer] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const canManage = accessLevel === "manage";

  const loadMeta = useCallback(() => {
    axios.get(`${API}/org/support/meta`, { withCredentials: true })
      .then(({ data }) => setMeta(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const openNewTicket = (customer) => { setNewTicketCustomer(customer || null); setNewTicketOpen(true); };
  const bumpRefresh = () => setRefreshKey((k) => k + 1);

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="support-error">{error}</p>;
  if (!meta) return <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="support-loading">Loading…</p>;

  return (
    <div data-testid="support-workspace">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 dark:border-white/10" data-testid="support-tabs">
        {TAB_DEFS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`support-tab-${t.id}`}
            className={`flex items-center gap-1.5 border-b-2 px-3.5 py-2.5 text-sm font-bold transition-colors duration-150 ${tab === t.id ? "border-[#0052FF] text-[#0052FF]" : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"}`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "overview" && <SupportOverviewTab API={API} meta={meta} canManage={canManage} onNewTicket={openNewTicket} refreshKey={refreshKey} onGoToTickets={() => setTab("tickets")} />}
        {tab === "tickets" && <TicketsTab API={API} meta={meta} canManage={canManage} onNewTicket={openNewTicket} refreshKey={refreshKey} />}
        {tab === "warranty" && <WarrantyTab API={API} canManage={canManage} refreshKey={refreshKey} />}
        {tab === "feedback" && <FeedbackTab API={API} meta={meta} canManage={canManage} refreshKey={refreshKey} />}
        {tab === "follow_ups" && <FollowUpsTab API={API} meta={meta} canManage={canManage} refreshKey={refreshKey} />}
        {tab === "communication" && <CommunicationTab API={API} canManage={canManage} />}
      </div>

      {newTicketOpen && (
        <NewTicketModal
          API={API}
          meta={meta}
          initialCustomer={newTicketCustomer}
          onClose={() => setNewTicketOpen(false)}
          onCreated={() => { setNewTicketOpen(false); bumpRefresh(); }}
        />
      )}
    </div>
  );
};
